import React, { useCallback, useEffect, useRef, useState } from 'react';
import { KeepAlive, useActivate, useUnactivate } from 'react-activation';
import { useInView } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { createSelector } from 'reselect';
import { downloadAddSoft } from '../../actions/downloadTask';
import { logging, logPostInfo } from '../../actions/log';
import Loading from '../../components/Loading/Loading';
import SoftDetailSwiper from '../../components/SoftMetaSwiper/SoftMetaSwiper';
import TopImgVideo from '../../components/TopImgVideo/TopImgVideo';
import { CONTEXT } from '../../config';
import { detailTab } from '../../constants/storageKeys';
import ForumAdvert from '../../static/forum_advert.svg';
import useLoginStatus, { handleLogin } from '../../utils/hooks/useLoginStatus';
import { getQueryString, logNative } from '../../utils/utils';
import Config from './config';
import ForumList from './ForumList';
import styles from './SoftDetail.module.less';
import SoftDetailGift from './SoftDetailGift';
import SoftDetailInfos from './SoftDetailInfos';
import SoftDetailMore from './SoftDetailMore';
import SoftDetailSimple from './SoftDetailSimple';
import SoftRecommend from './SoftRecommend';
import Comment from '../../components/Comment/Comment';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectUpgradeSofts = createSelector(
  state => state.native,
  native => native.upgradeSofts,
);

const selectSilentInstSoft = createSelector(
  state => state.native,
  native => native.supportSilentInstSoft,
);

const selectForum = createSelector(
  state => state.forum,
  forum => forum,
);
const selectLayoutInfo = createSelector(
  state => state.layout,
  layout => ({
    topImgVideo: layout.topImgVideo,
    isEnabledSilent: layout.isEnabledSilent,
  }),
);

const selectDownloadSofts = createSelector(
  state => state.downloadTask,
  downloadTask => downloadTask.softs,
);

function Detail(props) {
  const { match } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const logInfoData = useSelector(selectLogInfo);
  const location = useLocation();
  const upgradeSofts = useSelector(selectUpgradeSofts); // 可更新
  const silentInstSoft = useSelector(selectSilentInstSoft); // 支持快速静默安装的应用列表
  const { isEnabledSilent } = useSelector(selectLayoutInfo); // 是否打开快速静默安装\ 头部图片视频
  const [logFlag, setLogFlag] = useState(false); // 只报一次的桩
  const detailVideo = useRef(null);
  const [showContent, setShowContent] = useState('detail'); // 展示内容
  const [isPlay, setIsPlay] = useState(true); // 视频是否正在播放
  const showCommentTime = useRef(null);
  const node = useRef(null);
  const [videoInfo, setVideoInfo] = useState({ showBtn: false, showSimple: false });
  const [appDetailsData, setAppDetailsData] = useState(null); // 详情数据
  const [detailGift, setDetailGift] = useState(null); //只为拿取礼包数
  const [forumRelated, setForumRelated] = useState({});
  const loginStatus = useLoginStatus();
  const [releaseInfo, setReleaseInfo] = useState(null);
  const initTime = useRef(Date.now());
  const [isClick, setIsClick] = useState(false); // 判断按钮是否被点击
  const downloadList = useSelector(selectDownloadSofts);
  const [isVideoShow, setIsVideoShow] = useState(false); // 判断视频是否在可视区域内
  const [transmissionScore, setTransmissionScore] = useState(null); // 传入的分数
  const [afferentShow, setAfferentShow] = useState(false); // 是否有传入comment

  const [ref, inView] = useInView({
    threshold: 0,
  });

  /**
   * 统计页面浏览时长
   */
  const handlePvTimelog = useCallback(() => {
    const leaveTime = +new Date();
    dispatch(
      logging({
        category: 'I',
        action: 'View_Time',
        label: '',
        param2: {
          source: `AppDetail`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          initTime: initTime.current,
          leaveTime,
          time: leaveTime - initTime.current,
          softID: match.params.id,
        },
        bizInfo: match.params.bizInfo,
      }),
    );
  }, []);
  useEffect(() => {
    pagePvLog();
    initTime.current = Date.now();
    return () => {
      handlePvTimelog();
    };
  }, []);
  useActivate(() => {
    pagePvLog();
    initTime.current = +new Date();
  });
  useUnactivate(() => {
    handlePvTimelog();
  });

  const pagePvLog = useCallback(() => {
    // 推荐页面PV打桩  必须设备信息获取成功之后进行
    const param2 = {
      source: 'AppDetail',
      source_p: logInfoData.source_p,
      source_pp: logInfoData.source_pp,
      softid: match.params.id || '',
      softType: '0',
    };
    // 搜索结果也跳转过来的，带上附加信息
    if (logInfoData.source === 'Search' || logInfoData.source_p === 'Search') {
      param2.search_type = logInfoData.search_type;
      param2.search_word = logInfoData.search_word;
    }
    dispatch(
      logging({
        category: 'P',
        action: 'AppDetail',
        label: '',
        param2: param2,
        bizInfo: match.params.bizInfo,
      }),
    );
    dispatch(
      logPostInfo({
        source: 'AppDetail',
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
      }),
    );
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(async () => {
    // 获取软件详情数据数据
    try {
      const data = await Config.getAppDetails({
        softid: match.params.id || '',
        bizInfo: match.params.bizInfo || {},
      });
      setAppDetailsData(data);
    } catch (err) {
      logNative(err);
    }
  }, [match, loginStatus]);

  useEffect(() => {
    // 软件已经下架, 无效软件，后端未知已经下架
    if (
      appDetailsData &&
      appDetailsData.paramSoftId === match.params.id &&
      (appDetailsData.status === 500 || appDetailsData.status === -1) &&
      appDetailsData.loading === false
    ) {
      history.replace(`${CONTEXT}/error?sourcePage=ASoftDetail`);
    }
  }, [match, appDetailsData]);

  // 获取礼包
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(async () => {
    try {
      const data = await Config.getAppGift({
        androidType: '0',
        softInfo: match.params.id || '',
        bizInfo: match.params.bizInfo || {},
      });
      setDetailGift(data);
    } catch (error) {
      //
    }
  }, [match && match.params && match.params.id]);

  /**
   * 进入软件页面跳到精选评论位置
   */
  useEffect(() => {
    // 软件数据获取成功处理
    const goComment = getQueryString('goComment');
    if (
      appDetailsData &&
      appDetailsData.softID &&
      match.params.id === appDetailsData.softID &&
      goComment &&
      goComment !== '0'
    ) {
      handleChangeMenu('comment');
      setTimeout(() => {
        const el =
          document.querySelector(`[data-id='${goComment}']`) ||
          document.querySelector(`[id='soft_comment_list_header']`);

        el && el.scrollIntoView({ block: 'center' });
      }, 500);
    }
  }, [dispatch, appDetailsData]);

  /**
   * 进入软件页面并且开始下载, 等待更新列表、静默安装数据获取完成才开始下载
   */
  useEffect(() => {
    // 软件数据获取成功处理
    const isDownload = getQueryString('isDownload');
    const detailIsDownload = sessionStorage.getItem('detailIsDownload'); // 用于第一次进入页面就下载软件
    if (
      appDetailsData &&
      appDetailsData.softID &&
      match.params.id === appDetailsData.softID &&
      silentInstSoft &&
      silentInstSoft.length >= 0 &&
      upgradeSofts &&
      upgradeSofts.length != undefined &&
      (!detailIsDownload || detailIsDownload !== appDetailsData.softID)
    ) {
      // 如果URL上有isDownload=1, 则下载应用。(如果下载状态status有(1,2,3,4,5,6) 则当做已经下载过了的就不再下载)
      const status = parseInt(appDetailsData.status, 10) || 0;
      if (status === 0 && isDownload === '1') {
        downLoadInstallSoft(appDetailsData, match.params.bizInfo, upgradeSofts, silentInstSoft);
        sessionStorage.setItem('detailIsDownload', appDetailsData.softID);
        setIsClick(true);
      }
    }
  }, [dispatch, appDetailsData, upgradeSofts, silentInstSoft]);

  // 8.7带评分参数进入详情页进行打分
  useEffect(() => {
    // if (location.search) {
    // 获取是否打开评分
    let comment = getQueryString('comment');
    let score = getQueryString('score');
    if (location.search) {
      if (comment === '1') {
        setShowContent('comment');
        setAfferentShow(true);
        if (~~score >= 1 && ~~score <= 5) {
          setTransmissionScore(score);
        } else {
          setTransmissionScore('');
        }
        setTimeout(() => {
          const el = document.querySelector(`[id='soft_detail_comment']`);
          if (el) {
            const showEl = el.querySelector(`[id='soft_detail_comment_info']`);
            showEl && showEl.scrollIntoView({ block: 'center' });
          }
        }, 500);
      }
    }
  }, [location, loginStatus]);

  // 8.7需求触发下载滑动到相关推荐 单独处理没有放到button组件里面
  useEffect(() => {
    if (appDetailsData?.cover && appDetailsData?.cover?.showVideo === '1') {
      if (isClick && showContent === 'detail' && isVideoShow) {
        onDownloadSoft(downloadList);
      }
    } else {
      if (isClick && showContent === 'detail') {
        onDownloadSoft(downloadList);
      }
    }

    setIsClick(false);
  }, [isClick, showContent, downloadList, isVideoShow]);

  // 处理当前应用是否在下载中的问题,在下载中直接滑动到相关推荐
  const onDownloadSoft = list => {
    for (let i = 0; i < list?.length; i++) {
      if (list?.[i]?.type === 'pc') {
        if (
          list?.[i]?.pc?.SoftName === appDetailsData.softName &&
          list?.[i]?.attr.status !== 'err' &&
          list?.[i]?.attr.status !== 'dl' &&
          list?.[i]?.attr.status !== 'pause'
        ) {
          setTimeout(() => {
            const el = document.querySelector(`[id='detail_soft_recommend']`);
            el && el.scrollIntoView({ block: 'center' });
          }, 1000);
        }
      }
    }
  };

  // 进入详情页面直接下载
  const downLoadInstallSoft = useCallback(
    (softInfo, bizInfo, upgrade_softs, silent_soft) => {
      const upgrade = upgrade_softs.find(
        soft_ =>
          (soft_.androidType !== '1' &&
            (soft_.softID === softInfo.softID || soft_.SoftID === softInfo.softID)) ||
          (soft_.androidType === '1' && soft_.packageName === softInfo.packageName),
      );
      const logParam2 = {
        source: 'AppDetail',
        source_p: logInfoData.source_p,
        source_pp: logInfoData.source_pp,
        softid: softInfo.softID,
        softName: softInfo.softName,
        softType: '0',
        type: upgrade ? 'u' : 'd',
        url: softInfo.downLoadUrl || softInfo.downloadUrl,
      };
      const installparams = {};
      if (silent_soft && silent_soft.length > 0 && isEnabledSilent) {
        const silentSoft = silent_soft.find(soft_ => soft_.softID === softInfo.softID);
        if (silentSoft && silentSoft.softID) {
          logParam2.type = upgrade ? 'xu' : 'xd';
          if (silentSoft.silentParamDic) {
            installparams.quiet = silentSoft.silentParamDic;
          }
          if (silentSoft.silentDirParamDic) {
            installparams.todir = silentSoft.silentDirParamDic;
          }
        }
      }
      const installSoftInfo = { ...softInfo, logInfo: logParam2, bizInfo, installparams };
      dispatch(downloadAddSoft(installSoftInfo));
    },
    [dispatch],
  );

  // 切换详情和评论菜单
  const handleChangeMenu = useCallback(
    menu => {
      if (menu) {
        dispatch(
          logging({
            category: 'C',
            action: 'AppDetail_Tab',
            label: '',
            param2: {
              source: 'AppDetail',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              softid: appDetailsData.softID,
              softType: '0',
              tab: menu,
            },
            param3: appDetailsData.bizInfo,
          }),
        );
        setShowContent(menu || 'detail');
        let detailTabInfo = {};
        try {
          const info = sessionStorage.getItem(detailTab);
          if (info) {
            detailTabInfo = JSON.parse(info);
          }
        } catch (err) {
          detailTabInfo = {};
        } finally {
          detailTabInfo[appDetailsData.softID] = menu || 'detail';
          sessionStorage.setItem(detailTab, JSON.stringify(detailTabInfo));
        }
      }
    },
    [appDetailsData],
  );

  /**
   * 点击控制播放音乐
   */
  const onVideoClick = useCallback((play, mute) => {
    if (window.TopVideoPlayPause) {
      window.TopVideoPlayPause(!play, mute);
      setIsPlay(!play);
    }
  });

  // 获取相关推荐酷软全
  const getForumRelated = async () => {
    try {
      const data = await Config.getAppForumRelated({
        softId: appDetailsData.softID,
        skip: 0,
        limit: 24,
      });
      setForumRelated(data);
    } catch (err) {
      logNative(`获取相关酷软全出错 ${err}`);
    }
  };
  const handelMouseOver = useCallback(e => {}, []);
  const handleScroll = useCallback(
    e => {
      setVideoInfo(state => ({
        ...state,
        showSimple: +(e?.target?.scrollTop || 0) >= 309,
        scrollTop: +(e?.target?.scrollTop || 0),
      }));
      if (appDetailsData?.cover && appDetailsData?.cover.showVideo === '1') {
        setIsVideoShow(~~e?.target?.scrollTop >= 300);
      }
    },
    [appDetailsData],
  );
  useEffect(() => {
    if (appDetailsData && match.params.id === appDetailsData.softID) {
      getForumRelated();
    }
  }, [appDetailsData, match]);

  if (!appDetailsData || !appDetailsData.softID || match.params.id !== appDetailsData.softID) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  }

  return (
    <>
      <div className={styles.detail_video} ref={detailVideo}>
        {appDetailsData.cover && appDetailsData.cover.showVideo === '1' && inView && (
          <TopImgVideo page="detail" data={appDetailsData.cover} node={node.current} />
        )}
      </div>

      <div
        className={styles.detail}
        ref={node}
        onScroll={handleScroll}
        pagescroll="PCStore_Page_Scroll"
      >
        {appDetailsData.cover && appDetailsData.cover.showVideo === '1' && (
          <div
            className={styles.detail_video_lay}
            onMouseOver={e => {
              setVideoInfo(state => ({ ...state, showBtn: e?.clientY <= 329 }));
            }}
            onMouseLeave={() => {
              setVideoInfo({ ...videoInfo, showBtn: false });
            }}
          >
            <div
              className={`${isPlay ? styles.icon_v_play : styles.icon_v_stop} ${
                styles.detail_video_btn
              }`}
              onClick={e => {
                onVideoClick(isPlay, true);
                e.stopPropagation();
              }}
            />
          </div>
        )}

        <div
          className={styles.detail_box}
          style={
            {
              // paddingTop: appDetailsData?.cover?.showVideo === '1' ? '320px' : 0,
            }
          }
        >
          <div
            className={styles.detail_more}
            style={{
              opacity: inView ? 1 : 0,
            }}
            ref={ref}
          >
            <SoftDetailMore
              softDetails={appDetailsData}
              match={match}
              updateSoftDetails={data => {
                setAppDetailsData(data);
              }}
              handleDowm={e => setIsClick(e)}
            />
          </div>
          {!inView && (
            <div className={styles.detail_simple} style={{ opacity: !inView ? 1 : 0 }}>
              <SoftDetailSimple softDetails={appDetailsData} match={match} />
            </div>
          )}
          <div className={styles.detail_content}>
            <div
              className={styles.detail_menu}
              style={inView ? { position: 'relative', top: 0 } : {}}
            >
              <div
                className={
                  showContent === 'detail' ? 'detail_menu_item_active' : 'detail_menu_item'
                }
                onMouseOver={() => {
                  handleChangeMenu('detail');
                }}
                onFocus={() => undefined}
              >
                详情
              </div>
              <div
                className={
                  showContent === 'comment' ? 'detail_menu_item_active' : 'detail_menu_item'
                }
                onMouseOver={() => {
                  handleChangeMenu('comment');
                }}
                onFocus={() => undefined}
              >
                评论
                {releaseInfo?.commentNum > 0 ? `(${releaseInfo?.commentNum})` : ''}
              </div>
              {detailGift &&
                detailGift.softInfo === match.params.id &&
                detailGift.dataList &&
                detailGift.dataList.length > 0 && (
                  <div
                    className={
                      showContent === 'gift' ? 'detail_menu_item_active' : 'detail_menu_item'
                    }
                    onMouseOver={() => {
                      handleChangeMenu('gift');
                    }}
                    onFocus={() => undefined}
                  >
                    <span className="menu_gift" />
                    礼包
                  </div>
                )}
              <div
                className={showContent === 'forum' ? 'detail_menu_item_active' : 'detail_menu_item'}
                onMouseOver={() => {
                  handleChangeMenu('forum');
                }}
                onFocus={() => undefined}
              >
                一起聊
                <img src={ForumAdvert} alt="" className="menu_forum_advert" />
              </div>
            </div>

            {/* 具体内容对应的tab */}
            <div
              className={styles.detail_content}
              style={{ display: showContent === 'detail' ? 'flex' : 'none' }}
            >
              <div className={styles.detail_pic_view}>
                <SoftDetailSwiper
                  swiperData={appDetailsData.captureFileList}
                  appInfo={appDetailsData}
                />
              </div>
              <SoftDetailInfos appDetailsData={appDetailsData} />
              <div className={styles.detail_recommend}>
                <SoftRecommend softDetails={appDetailsData} />
              </div>
            </div>

            <div
              className={styles.detail_comment}
              style={{ display: showContent === 'comment' ? 'flex' : 'none' }}
            >
              {/* <DetailScore appDetailsData={appDetailsData} /> */}
              {/* 评论类型  1-应用评论  2-专题评论  3-帖子评论 */}
              <Comment
                data={{
                  type: '1',
                  // app: appDetailsData,
                  bizIdentity: appDetailsData?.softID,
                  tagCode: appDetailsData?.lenovoClassId,
                  version: appDetailsData?.version,
                }}
                scrollEl={node.current}
                handleReleaseInfo={data => {
                  setReleaseInfo(data);
                }}
                commentTargetInfo={{
                  targetId: appDetailsData?.softID,
                  targetName: appDetailsData?.softName,
                  targetCategoryCode: [appDetailsData?.lenovoClassId],
                  version: appDetailsData?.version,
                }}
                forum={{
                  hasForum: true,
                  softName: appDetailsData.softName,
                  content:
                    forumRelated.softId === appDetailsData.softID
                      ? forumRelated.forumList && forumRelated.forumList[0]
                      : null,
                }}
                afferentShow={afferentShow}
                controlAfferentShow={() => setAfferentShow(false)}
                transmissionScore={transmissionScore}
                emptyIsOpen={() => setTransmissionScore(null)}
                isScoreEmpty={true}
              />
            </div>
            <div
              className={styles.detail_content}
              style={{ display: showContent === 'forum' ? 'flex' : 'none' }}
            >
              <ForumList
                appDetailsData={appDetailsData}
                activeTab={showContent}
                forumRelated={forumRelated}
              />
            </div>
            {detailGift &&
              detailGift.softInfo === match.params.id &&
              detailGift.dataList &&
              detailGift.dataList.length > 0 && (
                <div
                  className={styles.detail_gift}
                  style={{ display: showContent === 'gift' ? 'flex' : 'none' }}
                >
                  <SoftDetailGift props={props} appDetailsData={appDetailsData} />
                  <div className={styles.detail_recommend}>
                    <SoftRecommend softDetails={appDetailsData} />
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </>
  );
}

export default props => {
  return (
    <KeepAlive name="detail" id={props?.match?.params?.id}>
      <Detail {...props} />
    </KeepAlive>
  );
};
